import React, { useState, useEffect, useCallback } from "react";
import { Typography, Button } from "@mui/material";
import Grid from "@mui/material/Grid2";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { defaultVariables } from "../../../../config";
import LoadingButton from "../../../components/elements/LoadingButton";
import OTPField from "../../../components/elements/fields/OTPField";
import { ArrowLeft } from "lucide-react";

const MarketingVerifyOtp = ({ rep, onVerified, onBack }) => {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const [fields, setFields] = useState({ token: "" });
    const [submitLoading, setSubmitLoading] = useState(false);
    const [timer, setTimer] = useState({ minutes: 2, seconds: 0 });

    useEffect(() => {
        if (timer.minutes === 0 && timer.seconds === 0) return;

        const interval = setInterval(() => {
            setTimer(({ minutes, seconds }) => {
                if (seconds > 0) return { minutes, seconds: seconds - 1 };
                if (minutes > 0) return { minutes: minutes - 1, seconds: 59 };
                clearInterval(interval);
                return { minutes: 0, seconds: 0 };
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [timer]);

    useEffect(() => {
        if (fields.token.length === 5) {
            setSubmitLoading(true);
            handleRequest(fields.token);
        }
    }, [fields.token]);

    const handleChange = (otp) => {
        setFields({ token: otp });
    };

    const handleRequest = async (token) => {
        try {
            const response = await axios.post(
                `${defaultVariables.url}/marketing/auth/verify-otp`,
                { rep_id: rep.rep_id, token },
                { headers: { Accept: "application/json" } }
            );
            onVerified(response.data);
        } catch (error) {
            enqueueSnackbar(t("wrongToken"), { variant: "error" });
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleResendToken = useCallback(async () => {
        setSubmitLoading(true);
        try {
            await axios.post(
                `${defaultVariables.url}/marketing/auth/send-otp`,
                { mobile: rep.mobile, code: "90" },
                { headers: { Accept: "application/json" } }
            );
            enqueueSnackbar(t("sendTokenTextResend"), { variant: "info" });
            setTimer({ minutes: 2, seconds: 0 });
        } catch (error) {
            enqueueSnackbar(t("wrongMobile"), { variant: "error" });
        } finally {
            setSubmitLoading(false);
        }
    }, [rep?.mobile, enqueueSnackbar, t]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (fields.token.length === 5) {
            setSubmitLoading(true);
            handleRequest(fields.token);
        }
    };

    return (
        <form noValidate onSubmit={handleSubmit}>
            <Grid container spacing={2}>
                <Grid item size={12}>
                    <Button startIcon={<ArrowLeft />} onClick={onBack}>
                        {t("back")}
                    </Button>
                </Grid>
                <Grid item size={12}>
                    <OTPField length={5} onChange={handleChange} />
                </Grid>
                <Grid item size={12} style={{ textAlign: "center" }}>
                    <Typography color="textSecondary">
                        {t("sendTokenText1")} <strong dir="ltr">{rep?.mobile}</strong>
                    </Typography>
                    {timer.minutes !== 0 || timer.seconds !== 0 ? (
                        <Typography color="textSecondary">
                            {t("sendTokenText2")}:{" "}
                            <strong style={{ width: 45, display: "inline-block" }}>
                                {timer.minutes}:{timer.seconds < 10 ? `0${timer.seconds}` : timer.seconds}
                            </strong>
                        </Typography>
                    ) : (
                        <Button
                            style={{ marginTop: 10 }}
                            color="secondary"
                            variant="outlined"
                            onClick={handleResendToken}
                        >
                            {t("sendAgain")}
                        </Button>
                    )}
                </Grid>
                <Grid item size={12}>
                    <LoadingButton
                        type="submit"
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth
                        loading={submitLoading}
                        style={{ width: "100%" }}
                        styleForParent={{ margin: "10px 0" }}
                    >
                        {t("confirm")}
                    </LoadingButton>
                </Grid>
            </Grid>
        </form>
    );
};

export default MarketingVerifyOtp;
